/**
 * RAC Client Journey Utopia — tenant feature-flag keys.
 *
 * TS constants map 1:1 to `public.feature_flags.flag_key` snake_case values.
 * All flags default OFF until explicitly enabled per tenant (or globally).
 */

export const RAC_UTOPIA_FLAG_DB_KEYS = {
  RAC_JOURNEY_DEFAULT_TAB: 'rac_journey_default_tab',
  RAC_UNIFIED_ACTION_QUEUE: 'rac_unified_action_queue',
  RAC_OVERVIEW_CHROME: 'rac_overview_chrome',
  RAC_LIFECYCLE_PANEL: 'rac_lifecycle_panel',
  RAC_COMPOSE_DEFAULT_REDIRECT: 'rac_compose_default_redirect',
  CO_ENROLLED_RETAIN_SAH_FINANCE_TABS: 'co_enrolled_retain_sah_finance_tabs',
} as const;

export type RacUtopiaFlagKey = keyof typeof RAC_UTOPIA_FLAG_DB_KEYS;

export const RAC_UTOPIA_FLAG_KEYS = Object.keys(RAC_UTOPIA_FLAG_DB_KEYS) as RacUtopiaFlagKey[];

export const RAC_UTOPIA_FLAG_DESCRIPTIONS: Record<RacUtopiaFlagKey, string> = {
  RAC_JOURNEY_DEFAULT_TAB:
    'Default RAC client records to the Residential tab (?tab=rac-hub) when no tab is specified.',
  RAC_UNIFIED_ACTION_QUEUE:
    'Use the unified RAC action queue on Residential hub, workflow rail, and Overview follow-ups.',
  RAC_OVERVIEW_CHROME: 'Show RAC status header and primary action chip on the client Overview tab.',
  RAC_LIFECYCLE_PANEL: 'Show RacClientJourneyPanel lifecycle strip on the Residential hub.',
  RAC_COMPOSE_DEFAULT_REDIRECT:
    'Route RAC care-plan continue paths to Plan Workspace (/compose) by default.',
  CO_ENROLLED_RETAIN_SAH_FINANCE_TABS:
    'Retain SAH finance tabs for co-enrolled clients when Residential hub chrome is visible.',
};

export type RacUtopiaFlags = Record<RacUtopiaFlagKey, boolean>;

export const RAC_UTOPIA_FLAGS_DEFAULT: RacUtopiaFlags = {
  RAC_JOURNEY_DEFAULT_TAB: false,
  RAC_UNIFIED_ACTION_QUEUE: false,
  RAC_OVERVIEW_CHROME: false,
  RAC_LIFECYCLE_PANEL: false,
  RAC_COMPOSE_DEFAULT_REDIRECT: false,
  CO_ENROLLED_RETAIN_SAH_FINANCE_TABS: true, // D12
};

export function isRacUtopiaFlagKey(value: string): value is RacUtopiaFlagKey {
  return (RAC_UTOPIA_FLAG_KEYS as readonly string[]).includes(value);
}

export function racUtopiaDbKey(flag: RacUtopiaFlagKey): string {
  return RAC_UTOPIA_FLAG_DB_KEYS[flag];
}

/**
 * Resolve a utopia flag from a merged flag map (tenant overrides global).
 * Missing keys resolve to false — fail closed until rollout.
 */
export function isRacUtopiaFlagEnabled(
  flags: Partial<RacUtopiaFlags> | null | undefined,
  key: RacUtopiaFlagKey,
): boolean {
  return Boolean(flags?.[key]);
}

/**
 * The record shell may replace the RAC hub command panel only when both rollout
 * flags are active. Keeping this decision shared prevents a flag combination
 * from hiding the old owner before Zone B has assumed the unified queue.
 */
export function racRecordShellOwnsActionQueue(
  flags: Partial<RacUtopiaFlags> | null | undefined,
): boolean {
  return (
    isRacUtopiaFlagEnabled(flags, 'RAC_UNIFIED_ACTION_QUEUE') &&
    isRacUtopiaFlagEnabled(flags, 'RAC_OVERVIEW_CHROME')
  );
}

export function mergeRacUtopiaFlags(
  partial: Partial<RacUtopiaFlags> | null | undefined,
): RacUtopiaFlags {
  return { ...RAC_UTOPIA_FLAGS_DEFAULT, ...partial };
}
